import { useCallback } from 'react';

import { RECAPTCHA_ENTERPRISE_SITE_KEY } from '@config/constants';
import { isTesting } from '@lib/environment/isTesting';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { logger } from '@lib/logger/logger';

export type RecaptchaEnterpriseAction = 'login' | 'registration' | 'edit_profile';

interface UseRecaptchaEnterpriseResult {
  /**
   * Executes the reCAPTCHA Enterprise check and returns a token
   * @param action - The action name to associate with this token
   * @returns A promise that resolves with the token string
   */
  executeRecaptcha: (action: RecaptchaEnterpriseAction) => Promise<string>;
  /**
   * Whether the reCAPTCHA Enterprise script is loaded and ready
   */
  isReady: boolean;
}

/**
 * Hook for using Google reCAPTCHA Enterprise v3.
 *
 * In development and testing environments, returns a mock token without making API calls.
 *
 * @example
 * ```tsx
 * const { executeRecaptcha, isReady } = useRecaptchaEnterprise();
 *
 * const handleSubmit = async () => {
 *   if (!isReady) {
 *     console.error('reCAPTCHA not ready');
 *     return;
 *   }
 *
 *   try {
 *     const token = await executeRecaptcha('edit_profile');
 *     // Send token to backend for validation
 *   } catch (error) {
 *     console.error('reCAPTCHA error:', error);
 *   }
 * };
 * ```
 */
export function useRecaptchaEnterprise(): UseRecaptchaEnterpriseResult {
  const clientHasRendered = useClientHasRendered();

  const isReady =
    clientHasRendered &&
    typeof window !== 'undefined' &&
    typeof window.grecaptcha?.enterprise?.execute === 'function';

  const executeRecaptcha = useCallback(
    async (action: RecaptchaEnterpriseAction): Promise<string> => {
      if (isTesting()) {
        logger.info(`[DEV] Mock reCAPTCHA Enterprise token generated for action: ${action}`);
        return `mock_recaptcha_enterprise_token_${action}_${Date.now()}`;
      }

      if (!isReady) {
        throw new Error('reCAPTCHA Enterprise is not ready. Ensure the script has loaded.');
      }

      try {
        const token = await window.grecaptcha!.enterprise!.execute(RECAPTCHA_ENTERPRISE_SITE_KEY, {
          action,
        });
        return token;
      } catch (error) {
        logger.error('reCAPTCHA Enterprise execution error:', error);
        throw new Error('Failed to execute reCAPTCHA Enterprise');
      }
    },
    [isReady],
  );

  return {
    executeRecaptcha,
    isReady: isReady || isTesting(),
  };
}
